import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowsClockwise, SignOut, MagnifyingGlass, EnvelopeSimple, Phone,
  CalendarBlank, MapPin, Trash, CheckCircle, Spinner, DownloadSimple,
  Briefcase, CreditCard, Wrench, CurrencyDollar, Star, ChatCircleDots,
  Receipt, PaperPlaneTilt, Copy,
  Storefront, PencilSimple, Plus, Eye, EyeSlash, ArrowUpRight,
  ChartLineUp, Users, Eye as EyeIcon, TrendUp,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REQ_STATUSES = [
  { v: "new", label: "New", color: "bg-[var(--ji-accent)] text-white" },
  { v: "contacted", label: "Contacted", color: "bg-blue-100 text-blue-900" },
  { v: "scheduled", label: "Scheduled", color: "bg-amber-100 text-amber-900" },
  { v: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-900" },
  { v: "closed", label: "Closed", color: "bg-stone-200 text-stone-700" },
];

const APP_STATUSES = [
  { v: "new", label: "New" },
  { v: "reviewing", label: "Reviewing" },
  { v: "contacted", label: "Contacted" },
  { v: "hired", label: "Hired" },
  { v: "rejected", label: "Rejected" },
  { v: "closed", label: "Closed" },
];

const badge = (list, s) => list.find((x) => x.v === s) || list[0];

// ---------- Sub-panels ----------

const ServiceRequestsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/service-requests");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load requests");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((i) => {
      if (filter !== "all" && (i.status || "new") !== filter) return false;
      if (!ql) return true;
      return [i.name, i.email, i.phone, i.appliance_type, i.address, i.description].join(" ").toLowerCase().includes(ql);
    });
  }, [items, q, filter]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/service-requests/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(`Status updated to ${status}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try {
      await api.delete(`/admin/service-requests/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="panel-requests" className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
          <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, appliance…" className="h-11 pl-9" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="admin-status-filter" className="md:w-56 h-11"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {REQ_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
      </div>

      {loading ? <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div> :
        filtered.length === 0 ? <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>No requests.</div> :
        filtered.map((it) => {
          const b = badge(REQ_STATUSES, it.status);
          const isOpen = openId === it.id;
          return (
            <div key={it.id} data-testid={`admin-row-${it.id}`} className="ji-card p-5" style={{ borderRadius: 4 }}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${b.color}`} style={{ borderRadius: 4 }}>{b.label}</span>
                    {it.bought_from_shop && (
                      <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 bg-[var(--ji-accent)] text-white font-bold" style={{ borderRadius: 4 }} data-testid={`admin-shop-badge-${it.id}`}>
                        Shop · 10% off
                      </span>
                    )}
                  </div>
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold tracking-tight mt-2">{it.name}</div>
                  <div className="text-xs text-[var(--ji-muted)] mt-1 inline-flex items-center gap-1"><CalendarBlank size={12} weight="bold" /> {new Date(it.created_at).toLocaleString()}</div>
                </div>
                <div className="md:col-span-4 text-sm space-y-1">
                  <div><span className="text-[var(--ji-muted)]">Service:</span> <strong>{it.service_type}</strong> · {it.appliance_type}</div>
                  <a href={`tel:${it.phone}`} className="inline-flex items-center gap-2 hover:text-[var(--ji-accent)]"><Phone size={14} weight="bold" /> {it.phone}</a>
                  <div><a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 hover:text-[var(--ji-accent)] break-all"><EnvelopeSimple size={14} weight="bold" /> {it.email}</a></div>
                  {it.address && <div className="inline-flex items-center gap-2 text-[var(--ji-muted)]"><MapPin size={14} weight="bold" /> {it.address}</div>}
                </div>
                <div className="md:col-span-4 flex flex-col gap-2 items-start md:items-end">
                  <Select value={it.status || "new"} onValueChange={(v) => updateStatus(it.id, v)}>
                    <SelectTrigger data-testid={`admin-status-${it.id}`} className="w-full md:w-48 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{REQ_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <button onClick={() => setOpenId(isOpen ? null : it.id)} className="ji-btn-outline px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>{isOpen ? "Hide" : "Details"}</button>
                    <button onClick={() => remove(it.id)} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold text-red-600 hover:bg-red-50" style={{ borderRadius: 4 }}>
                      <Trash size={14} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="mt-5 pt-5 border-t border-[var(--ji-border)] text-sm">
                  <div className="ji-overline mb-2">Description</div>
                  <p className="whitespace-pre-wrap leading-relaxed">{it.description}</p>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

const CareerAppsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/career-applications");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load applications");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/career-applications/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(`Marked ${status}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application?")) return;
    try {
      await api.delete(`/admin/career-applications/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  const downloadResume = async (id, filename) => {
    try {
      const token = localStorage.getItem("ji_token");
      const res = await fetch(`${API}/admin/career-applications/${id}/resume`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("No resume on file");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "resume"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || "Download failed"); }
  };

  return (
    <div data-testid="panel-careers" className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
      </div>
      {loading ? <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div> :
        items.length === 0 ? <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>No applications yet.</div> :
        items.map((it) => {
          const isOpen = openId === it.id;
          return (
            <div key={it.id} data-testid={`admin-app-row-${it.id}`} className="ji-card p-5" style={{ borderRadius: 4 }}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-4">
                  <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 bg-[var(--ji-accent)] text-white" style={{ borderRadius: 4 }}>{it.status}</span>
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold tracking-tight mt-2">{it.name}</div>
                  <div className="text-xs text-[var(--ji-muted)] mt-1"><CalendarBlank size={12} weight="bold" className="inline mr-1" /> {new Date(it.created_at).toLocaleString()}</div>
                </div>
                <div className="md:col-span-4 text-sm space-y-1">
                  <div className="inline-flex items-center gap-2"><Phone size={14} weight="bold" /> {it.phone}</div>
                  {it.email && <div><a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 hover:text-[var(--ji-accent)] break-all"><EnvelopeSimple size={14} weight="bold" /> {it.email}</a></div>}
                  <div className="inline-flex items-center gap-2 text-[var(--ji-muted)]"><MapPin size={14} weight="bold" /> {it.address}</div>
                </div>
                <div className="md:col-span-4 flex flex-col gap-2 items-start md:items-end">
                  <Select value={it.status || "new"} onValueChange={(v) => updateStatus(it.id, v)}>
                    <SelectTrigger className="w-full md:w-48 h-10"><SelectValue /></SelectTrigger>
                    <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setOpenId(isOpen ? null : it.id)} data-testid={`admin-app-toggle-${it.id}`} className="ji-btn-outline px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>{isOpen ? "Hide" : "Details"}</button>
                    {it.resume_filename && (
                      <button onClick={() => downloadResume(it.id, it.resume_filename)} data-testid={`admin-app-resume-${it.id}`} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                        <DownloadSimple size={14} weight="bold" /> Resume
                      </button>
                    )}
                    <button onClick={() => remove(it.id)} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold text-red-600 hover:bg-red-50" style={{ borderRadius: 4 }}>
                      <Trash size={14} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
              {isOpen && (
                <div className="mt-5 pt-5 border-t border-[var(--ji-border)] grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="ji-overline mb-2">Professional Reference</div>
                    <p className="whitespace-pre-wrap leading-relaxed text-[var(--ji-text)]">{it.professional_reference}</p>
                  </div>
                  <div>
                    <div className="ji-overline mb-2">Recent Job</div>
                    <p className="whitespace-pre-wrap leading-relaxed text-[var(--ji-text)]">{it.recent_job}</p>
                  </div>
                  {it.notes && (
                    <div className="md:col-span-2">
                      <div className="ji-overline mb-2">Notes</div>
                      <p className="whitespace-pre-wrap leading-relaxed text-[var(--ji-text)]">{it.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

const PaymentsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both legacy Stripe diagnostic payments and new Square diagnostic bookings.
      const [stripeRes, squareRes] = await Promise.all([
        api.get("/admin/payments").catch(() => ({ data: [] })),
        api.get("/admin/square-payments").catch(() => ({ data: [] })),
      ]);
      const stripe = (stripeRes.data || []).map((p) => ({
        id: p.id,
        source: "stripe",
        status: p.status,
        payment_status: p.payment_status,
        amount: p.amount,
        kind: p.kind || "booking",
        package_label: p.package_label,
        customer_name: p.customer_name,
        customer_email: p.customer_email,
        customer_phone: p.customer_phone,
        address: p.address,
        preferred_date: p.preferred_date,
        notes: p.notes,
        session_id: p.session_id,
        created_at: p.created_at,
      }));
      const square = (squareRes.data || [])
        .filter((p) => p.source === "diagnostic-booking")
        .map((p) => ({
          id: p.id,
          source: "square",
          status: p.status,
          payment_status: p.status === "paid" ? "paid" : "unpaid",
          amount: p.total_amount,
          kind: "Square · diagnostic",
          package_label: p.package_label,
          customer_name: p.customer_name,
          customer_email: p.customer_email,
          customer_phone: p.customer_phone,
          address: p.address,
          preferred_date: p.preferred_date,
          notes: p.notes,
          session_id: p.id,
          created_at: p.created_at,
        }));
      const merged = [...stripe, ...square].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(merged);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load payments");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const badgeColor = (s, ps) => {
    if (ps === "paid") return "bg-emerald-100 text-emerald-900";
    if (s === "expired" || s === "cancelled" || s === "failed") return "bg-red-100 text-red-900";
    return "bg-amber-100 text-amber-900";
  };

  return (
    <div data-testid="panel-payments" className="space-y-4">
      <div className="flex justify-end">
        <button onClick={load} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
      </div>
      {loading ? <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div> :
        items.length === 0 ? <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>No payments yet.</div> :
        items.map((it) => (
          <div key={it.id} data-testid={`admin-payment-row-${it.id}`} className="ji-card p-5" style={{ borderRadius: 4 }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-3">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${badgeColor(it.status, it.payment_status)}`} style={{ borderRadius: 4 }}>
                  {it.payment_status === "paid" ? "Paid" : it.status}
                </span>
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]">${Number(it.amount).toFixed(2)}</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)] mt-1">{it.kind}</div>
              </div>
              <div className="md:col-span-5 text-sm space-y-1">
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold tracking-tight">{it.package_label || it.invoice_reference || "Payment"}</div>
                <div><strong>{it.customer_name}</strong></div>
                <div><a href={`mailto:${it.customer_email}`} className="hover:text-[var(--ji-accent)] break-all inline-flex items-center gap-1"><EnvelopeSimple size={12} weight="bold" /> {it.customer_email}</a></div>
                {it.customer_phone && <div className="inline-flex items-center gap-1"><Phone size={12} weight="bold" /> {it.customer_phone}</div>}
                {it.address && <div className="text-[var(--ji-muted)] inline-flex items-center gap-1"><MapPin size={12} weight="bold" /> {it.address}</div>}
              </div>
              <div className="md:col-span-4 text-xs text-[var(--ji-muted)] space-y-1">
                <div><CalendarBlank size={12} weight="bold" className="inline mr-1" /> {new Date(it.created_at).toLocaleString()}</div>
                {it.preferred_date && <div>Preferred: {it.preferred_date}</div>}
                {it.notes && <div className="mt-2 pt-2 border-t border-[var(--ji-border)]"><span className="font-medium text-[var(--ji-text)]">Notes:</span> {it.notes}</div>}
                <div className="mt-2 font-mono text-[10px] break-all">{it.session_id}</div>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

// Reviews moderation panel
const REVIEW_STATUS_FILTERS = [
  { v: "pending", label: "Pending" },
  { v: "approved", label: "Approved" },
  { v: "rejected", label: "Rejected" },
  { v: "all", label: "All" },
];

const ReviewsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending"); // pending-first so nothing hides
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/reviews"); setItems(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load reviews"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${id}`, { status });
      setItems((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`Review ${status}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${id}`); setItems((p) => p.filter((r) => r.id !== id)); toast.success("Deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };
  const badge = { pending: "bg-amber-100 text-amber-900", approved: "bg-emerald-100 text-emerald-900", rejected: "bg-red-100 text-red-900" };

  const counts = useMemo(() => {
    const c = { all: items.length, pending: 0, approved: 0, rejected: 0 };
    items.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [items]);

  // Pending-first sort, then newest — guarantees pending reviews rise to the top.
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const scoped = filter === "all" ? items : items.filter((r) => (r.status || "pending") === filter);
    const searched = !ql ? scoped : scoped.filter((r) =>
      [r.name, r.email, r.city, r.service_used, r.quote].filter(Boolean).join(" ").toLowerCase().includes(ql)
    );
    return [...searched].sort((a, b) => {
      const aPending = a.status === "pending" ? 0 : 1;
      const bPending = b.status === "pending" ? 0 : 1;
      if (aPending !== bPending) return aPending - bPending;
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [items, filter, q]);

  return (
    <div data-testid="panel-reviews" className="space-y-4">
      {/* Diagnostic header — total counts always visible */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-2 border-b border-[var(--ji-border)]">
        <div className="text-sm">
          <span className="ji-overline">In this database:</span>{" "}
          <span data-testid="reviews-total-count" className="font-semibold text-[var(--ji-text)]">{counts.all} total</span>
          <span className="text-[var(--ji-muted)]">
            {" "}·{" "}
            <span data-testid="reviews-pending-count-tab" className="font-semibold text-[var(--ji-accent)]">{counts.pending} pending</span>
            {" "}·{" "}
            {counts.approved} approved
            {" "}·{" "}
            {counts.rejected} rejected
          </span>
        </div>
        <button onClick={load} data-testid="reviews-refresh" className="ji-btn-outline inline-flex items-center gap-2 px-4 h-10 text-sm font-semibold self-start md:self-auto" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
      </div>

      {/* Filter chips + search */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex flex-wrap gap-1">
          {REVIEW_STATUS_FILTERS.map((f) => (
            <button
              key={f.v}
              data-testid={`reviews-filter-${f.v}`}
              onClick={() => setFilter(f.v)}
              className={`text-xs uppercase tracking-[0.15em] px-3 py-2 font-semibold transition-colors ${
                filter === f.v ? "bg-[var(--ji-primary)] text-white" : "bg-[var(--ji-surface-alt)] text-[var(--ji-muted)] hover:text-[var(--ji-text)]"
              }`}
              style={{ borderRadius: 4 }}
            >
              {f.label} <span className="opacity-70 ml-1">{counts[f.v] || 0}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 md:max-w-md md:ml-auto">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
          <Input data-testid="reviews-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, quote, city…" className="h-10 pl-9" />
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /></div>
      ) : filtered.length === 0 ? (
        <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }} data-testid="reviews-empty">
          {counts.all === 0
            ? "No reviews in the database yet. As soon as a customer submits at /reviews, they'll appear here — no matter the status."
            : `No ${filter === "all" ? "" : filter + " "}reviews match. Try a different filter above.`}
        </div>
      ) : (
        filtered.map((r) => (
          <div key={r.id} data-testid={`admin-review-row-${r.id}`} className="ji-card p-5" style={{ borderRadius: 4 }}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
              <div className="md:col-span-4">
                <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${badge[r.status] || badge.pending}`} style={{ borderRadius: 4 }}>{r.status}</span>
                <div className="mt-2 flex gap-0.5">
                  {[...Array(5)].map((_, k) => <Star key={k} size={14} weight={k < r.rating ? "fill" : "regular"} className={k < r.rating ? "text-[var(--ji-accent)]" : "text-[var(--ji-border)]"} />)}
                </div>
                <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold tracking-tight mt-2">{r.name}</div>
                <div className="text-xs text-[var(--ji-muted)]">{r.city || "—"} · {r.service_used || "—"}</div>
                <div className="text-[10px] text-[var(--ji-muted)] mt-1"><CalendarBlank size={10} weight="bold" className="inline mr-1" /> {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <div className="md:col-span-5 text-sm whitespace-pre-wrap leading-relaxed">&ldquo;{r.quote}&rdquo;</div>
              <div className="md:col-span-3 flex flex-col gap-2">
                <button onClick={() => setStatus(r.id, "approved")} data-testid={`admin-review-approve-${r.id}`} disabled={r.status === "approved"} className="ji-btn-accent inline-flex items-center justify-center gap-1 px-3 h-9 text-xs font-semibold disabled:opacity-40" style={{ borderRadius: 4 }}>Approve</button>
                <button onClick={() => setStatus(r.id, "rejected")} data-testid={`admin-review-reject-${r.id}`} disabled={r.status === "rejected"} className="ji-btn-outline inline-flex items-center justify-center gap-1 px-3 h-9 text-xs font-semibold disabled:opacity-40" style={{ borderRadius: 4 }}>Reject</button>
                <button onClick={() => remove(r.id)} className="ji-btn-outline inline-flex items-center justify-center gap-1 px-3 h-9 text-xs font-semibold text-red-600 hover:bg-red-50" style={{ borderRadius: 4 }}><Trash size={12} weight="bold" /> Delete</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ---------- Invoices (admin-issued "Pay by Email" links) ----------
const INVOICE_STATUSES = {
  initiated: "bg-amber-100 text-amber-900",
  paid: "bg-emerald-100 text-emerald-900",
  cancelled: "bg-red-100 text-red-900",
  failed: "bg-red-100 text-red-900",
};

const InvoiceModal = ({ open, onOpenChange, onCreated }) => {
  const empty = {
    customer_name: "", customer_email: "", customer_phone: "",
    description: "", invoice_reference: "", base_amount: "",
    send_email: true,
  };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { url, ...invoice }

  useEffect(() => {
    if (open) { setForm(empty); setResult(null); }
  }, [open]);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    if (!form.customer_name.trim() || !form.description.trim() || !form.base_amount) {
      toast.error("Customer name, description, and amount are required.");
      return;
    }
    if (form.send_email && !form.customer_email.trim()) {
      toast.error("Email address is required when sending by email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name.trim(),
        customer_email: form.customer_email.trim() || null,
        customer_phone: form.customer_phone.trim() || null,
        description: form.description.trim(),
        invoice_reference: form.invoice_reference.trim() || null,
        base_amount: Number(form.base_amount),
        send_email: !!form.send_email,
      };
      const { data } = await api.post("/admin/invoices", payload);
      setResult(data);
      toast.success(form.send_email ? "Invoice created & email sent." : "Invoice created.");
      onCreated && onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create invoice");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = () => {
    if (!result?.checkout_url) return;
    navigator.clipboard.writeText(result.checkout_url);
    toast.success("Link copied.");
  };

  const base = Number(form.base_amount) || 0;
  const surcharge = base > 0 ? +(base * 0.05).toFixed(2) : 0;
  const total = +(base + surcharge).toFixed(2);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invoice-modal" className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">
            {result ? "Invoice Ready" : "Send Invoice"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--ji-muted)]">
            {result
              ? "Payment link generated. Share the link below with your customer."
              : "Generate a Square payment link and optionally email it to your customer. A 5% card processing surcharge is added on top."}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="ji-overline mb-1 block">Customer Name *</label>
                <Input data-testid="invoice-name" value={form.customer_name} onChange={set("customer_name")} placeholder="Jane Smith" />
              </div>
              <div>
                <label className="ji-overline mb-1 block">Invoice Ref (optional)</label>
                <Input data-testid="invoice-ref" value={form.invoice_reference} onChange={set("invoice_reference")} placeholder="INV-1042" />
              </div>
              <div>
                <label className="ji-overline mb-1 block">Email {form.send_email && "*"}</label>
                <Input data-testid="invoice-email" type="email" value={form.customer_email} onChange={set("customer_email")} placeholder="jane@example.com" />
              </div>
              <div>
                <label className="ji-overline mb-1 block">Phone (optional)</label>
                <Input data-testid="invoice-phone" value={form.customer_phone} onChange={set("customer_phone")} placeholder="(504) 555-0123" />
              </div>
            </div>

            <div>
              <label className="ji-overline mb-1 block">Description *</label>
              <Textarea data-testid="invoice-description" value={form.description} onChange={set("description")} rows={3} placeholder="Courier run 3/15 — auto parts pickup from Lafayette to Kenner" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div>
                <label className="ji-overline mb-1 block">Base Amount ($) *</label>
                <Input data-testid="invoice-amount" type="number" min="0.01" step="0.01" value={form.base_amount} onChange={set("base_amount")} placeholder="85.00" />
              </div>
              <div className="text-sm text-[var(--ji-muted)] md:col-span-2 bg-[var(--ji-surface-alt)] p-3" style={{ borderRadius: 4 }}>
                <div>Base: <strong className="text-[var(--ji-text)]">${base.toFixed(2)}</strong></div>
                <div>5% surcharge: <strong className="text-[var(--ji-text)]">${surcharge.toFixed(2)}</strong></div>
                <div className="mt-1 text-base">Customer pays: <strong className="text-[var(--ji-primary)]">${total.toFixed(2)}</strong></div>
              </div>
            </div>

            <div className="flex flex-col gap-2 p-3 bg-[var(--ji-surface-alt)]" style={{ borderRadius: 4 }}>
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm">
                <input data-testid="invoice-send-email" type="checkbox" checked={form.send_email} onChange={set("send_email")} className="w-4 h-4 accent-[var(--ji-accent)]" />
                <span>Email the payment link to the customer</span>
              </label>
              <label className="inline-flex items-center gap-2 opacity-50 text-sm cursor-not-allowed">
                <input type="checkbox" disabled className="w-4 h-4" />
                <span>Text (SMS) the payment link — <em>coming soon</em></span>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--ji-border)]">
              <button onClick={() => onOpenChange(false)} className="ji-btn-outline px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }} disabled={submitting}>Cancel</button>
              <button data-testid="invoice-submit" onClick={submit} disabled={submitting} className="ji-btn-accent inline-flex items-center gap-2 px-5 h-10 text-sm font-semibold disabled:opacity-60" style={{ borderRadius: 4 }}>
                {submitting ? <Spinner size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="bold" />}
                {submitting ? "Creating…" : "Create & Send"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="invoice-result">
            <div className="ji-card p-4 bg-emerald-50 border-emerald-200" style={{ borderRadius: 4 }}>
              <div className="inline-flex items-center gap-2 text-emerald-900 font-semibold text-sm">
                <CheckCircle size={18} weight="fill" /> Payment link is live
              </div>
              <div className="mt-2 text-xs text-emerald-900/80">
                {result.sent_email_at ? "Email delivered to the customer." : "Copy the link below and share it however you prefer."}
              </div>
            </div>
            <div>
              <label className="ji-overline mb-1 block">Amount</label>
              <div className="text-2xl font-bold text-[var(--ji-primary)]" style={{ fontFamily: "Cabinet Grotesk" }}>${Number(result.total_amount).toFixed(2)}</div>
              <div className="text-xs text-[var(--ji-muted)]">Base ${Number(result.base_amount).toFixed(2)} + 5% surcharge ${Number(result.surcharge_amount).toFixed(2)}</div>
            </div>
            <div>
              <label className="ji-overline mb-1 block">Payment Link</label>
              <div className="flex gap-2">
                <Input readOnly value={result.checkout_url} data-testid="invoice-result-url" className="font-mono text-xs" />
                <button onClick={copyLink} data-testid="invoice-copy-link" className="ji-btn-outline inline-flex items-center gap-1 px-3 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>
                  <Copy size={14} weight="bold" /> Copy
                </button>
                <a href={result.checkout_url} target="_blank" rel="noreferrer" className="ji-btn-outline inline-flex items-center gap-1 px-3 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>Open</a>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[var(--ji-border)]">
              <button onClick={() => { setForm(empty); setResult(null); }} className="ji-btn-outline px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>Send Another</button>
              <button onClick={() => onOpenChange(false)} className="ji-btn-accent px-5 h-10 text-sm font-semibold" style={{ borderRadius: 4 }}>Done</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const InvoicesPanel = ({ onOpenModal, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invoices");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load invoices");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((i) =>
      [i.customer_name, i.customer_email, i.customer_phone, i.description, i.invoice_reference]
        .filter(Boolean).join(" ").toLowerCase().includes(ql)
    );
  }, [items, q]);

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied.");
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/invoices/${id}/resend-email`);
      toast.success("Email resent to the customer.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Resend failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice? The Square link itself will still exist until Square voids it.")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      setItems((p) => p.filter((r) => r.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="panel-invoices" className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
          <Input data-testid="invoices-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, email, ref…" className="h-11 pl-9" />
        </div>
        <button onClick={load} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
        <button data-testid="invoices-new-btn" onClick={onOpenModal} className="ji-btn-accent inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <PaperPlaneTilt size={16} weight="bold" /> New Invoice
        </button>
      </div>

      {loading ? <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div> :
        filtered.length === 0 ? (
          <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>
            No invoices yet. Click <strong>New Invoice</strong> to send one.
          </div>
        ) :
        filtered.map((it) => {
          const badgeCls = INVOICE_STATUSES[it.status] || INVOICE_STATUSES.initiated;
          return (
            <div key={it.id} data-testid={`invoice-row-${it.id}`} className="ji-card p-5" style={{ borderRadius: 4 }}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-3">
                  <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${badgeCls}`} style={{ borderRadius: 4 }}>
                    {it.status === "paid" ? "Paid" : it.status}
                  </span>
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]">
                    ${Number(it.total_amount).toFixed(2)}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)] mt-1">
                    Base ${Number(it.base_amount).toFixed(2)} + 5%
                  </div>
                  {it.invoice_reference && (
                    <div className="text-xs font-mono mt-2 text-[var(--ji-muted)]">{it.invoice_reference}</div>
                  )}
                </div>
                <div className="md:col-span-5 text-sm space-y-1">
                  <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-lg font-bold tracking-tight">{it.description}</div>
                  <div><strong>{it.customer_name}</strong></div>
                  {it.customer_email && <div><a href={`mailto:${it.customer_email}`} className="hover:text-[var(--ji-accent)] break-all inline-flex items-center gap-1"><EnvelopeSimple size={12} weight="bold" /> {it.customer_email}</a></div>}
                  {it.customer_phone && <div className="inline-flex items-center gap-1"><Phone size={12} weight="bold" /> {it.customer_phone}</div>}
                  <div className="text-xs text-[var(--ji-muted)] mt-2">
                    <CalendarBlank size={12} weight="bold" className="inline mr-1" /> {new Date(it.created_at).toLocaleString()}
                  </div>
                  {it.sent_email_at && (
                    <div className="text-xs text-emerald-700 inline-flex items-center gap-1 mt-1">
                      <CheckCircle size={12} weight="fill" /> Emailed {new Date(it.sent_email_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="md:col-span-4 flex flex-col gap-2 items-start md:items-end">
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button onClick={() => copyLink(it.checkout_url)} data-testid={`invoice-copy-${it.id}`} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                      <Copy size={12} weight="bold" /> Copy Link
                    </button>
                    <a href={it.checkout_url} target="_blank" rel="noreferrer" className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>Open</a>
                    {it.customer_email && (
                      <button onClick={() => resend(it.id)} data-testid={`invoice-resend-${it.id}`} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                        <EnvelopeSimple size={12} weight="bold" /> Resend
                      </button>
                    )}
                    <button onClick={() => remove(it.id)} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold text-red-600 hover:bg-red-50" style={{ borderRadius: 4 }}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
    </div>
  );
};

// ---------- Shop (products CRUD) ----------
const PRODUCT_CATEGORIES = [
  { id: "appliances", label: "Appliances" },
  { id: "appliance-parts", label: "Appliance Parts" },
  { id: "cleaning", label: "Cleaning Supplies" },
  { id: "furniture", label: "Household Furniture" },
  { id: "tools", label: "Tools" },
  { id: "electronics", label: "Electronics" },
];

const emptyProduct = {
  name: "", category: "appliances", brand: "", price: "",
  image_url: "", description: "", affiliate_url: "",
  supplier: "Amazon", badge: "", is_active: true,
  part_number: "", model_number: "", compatible_models_text: "",
  is_common: false, markup_pct: "",
};

const ProductModal = ({ open, onOpenChange, editing, onSaved }) => {
  const [form, setForm] = useState(emptyProduct);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? {
            ...emptyProduct,
            ...editing,
            price: String(editing.price ?? ""),
            markup_pct: editing.markup_pct == null ? "" : String(editing.markup_pct),
            compatible_models_text: Array.isArray(editing.compatible_models) ? editing.compatible_models.join(", ") : "",
          }
        : emptyProduct);
    }
  }, [open, editing]);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!form.name.trim() || !form.image_url.trim() || !form.affiliate_url.trim() || !form.price) {
      toast.error("Name, image URL, affiliate URL, and price are required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        brand: form.brand.trim() || null,
        price: Number(form.price),
        image_url: form.image_url.trim(),
        description: form.description.trim() || null,
        affiliate_url: form.affiliate_url.trim(),
        supplier: form.supplier.trim() || null,
        badge: form.badge.trim() || null,
        is_active: !!form.is_active,
        part_number: form.part_number.trim() || null,
        model_number: form.model_number.trim() || null,
        compatible_models: form.compatible_models_text
          .split(/[,\n]/).map((s) => s.trim()).filter(Boolean),
        markup_pct: form.markup_pct === "" ? null : Number(form.markup_pct),
        is_common: !!form.is_common,
      };
      if (editing?.id) {
        await api.patch(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product added.");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-modal" className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">
            {editing ? "Edit Product" : "Add Product"}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--ji-muted)]">
            Products appear on the public <strong>/shop</strong> page when Active. Customers click the &ldquo;Buy&rdquo; button and are sent to your supplier&rsquo;s page in a new tab.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="ji-overline mb-1 block">Product Name *</label>
              <Input data-testid="product-name" value={form.name} onChange={set("name")} placeholder="Whirlpool 25 cu. ft. French Door Refrigerator" />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Category *</label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="ji-overline mb-1 block">Brand</label>
              <Input data-testid="product-brand" value={form.brand} onChange={set("brand")} placeholder="Whirlpool" />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Price (USD) *</label>
              <Input data-testid="product-price" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} placeholder="1799.00" />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Supplier</label>
              <Input data-testid="product-supplier" value={form.supplier} onChange={set("supplier")} placeholder="Amazon" />
            </div>
            <div className="md:col-span-2">
              <label className="ji-overline mb-1 block">Image URL *</label>
              <Input data-testid="product-image" value={form.image_url} onChange={set("image_url")} placeholder="https://…jpg" />
              {form.image_url && (
                <img src={form.image_url} alt="preview" loading="lazy" className="mt-2 h-24 w-auto object-cover border border-[var(--ji-border)]" style={{ borderRadius: 4 }} onError={(e) => (e.target.style.display = "none")} />
              )}
            </div>
            <div className="md:col-span-2">
              <label className="ji-overline mb-1 block">Affiliate / Product URL *</label>
              <Input data-testid="product-affiliate" value={form.affiliate_url} onChange={set("affiliate_url")} placeholder="https://amazon.com/dp/…" />
            </div>
            <div className="md:col-span-2">
              <label className="ji-overline mb-1 block">Description</label>
              <Textarea data-testid="product-description" value={form.description} onChange={set("description")} rows={3} placeholder="Short blurb shown on the product card." />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Badge (optional)</label>
              <Input data-testid="product-badge" value={form.badge} onChange={set("badge")} placeholder="Best Seller · New · Sale" />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm h-11">
                <input data-testid="product-active" type="checkbox" checked={!!form.is_active} onChange={set("is_active")} className="w-4 h-4 accent-[var(--ji-accent)]" />
                <span>Active (visible on the /shop page)</span>
              </label>
            </div>
            <div className="md:col-span-2 pt-3 border-t border-[var(--ji-border)]">
              <div className="ji-overline mb-2">Parts / Search fields (optional — appliance-parts category)</div>
            </div>
            <div>
              <label className="ji-overline mb-1 block">Part Number</label>
              <Input data-testid="product-part-number" value={form.part_number} onChange={set("part_number")} placeholder="EDR1RXD1" />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Model Number</label>
              <Input data-testid="product-model-number" value={form.model_number} onChange={set("model_number")} placeholder="EDR1RXD1" />
            </div>
            <div className="md:col-span-2">
              <label className="ji-overline mb-1 block">Compatible Appliance Models (comma or newline separated)</label>
              <Textarea data-testid="product-compatible" rows={2} value={form.compatible_models_text} onChange={set("compatible_models_text")} placeholder="WRS325FDAM, WRS325SDHZ, WRF535SWHZ" />
            </div>
            <div>
              <label className="ji-overline mb-1 block">Markup %</label>
              <Input data-testid="product-markup" type="number" min="0" max="200" step="1" value={form.markup_pct} onChange={set("markup_pct")} placeholder="25 (default)" />
              <div className="text-[10px] text-[var(--ji-muted)] mt-1">Leave blank to use the global default (25%).</div>
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 cursor-pointer text-sm h-11">
                <input data-testid="product-common" type="checkbox" checked={!!form.is_common} onChange={set("is_common")} className="w-4 h-4 accent-[var(--ji-accent)]" />
                <span>Feature as &ldquo;common&rdquo; item</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--ji-border)]">
            <button type="button" onClick={() => onOpenChange(false)} className="ji-btn-outline px-4 h-10 text-sm font-semibold" style={{ borderRadius: 4 }} disabled={submitting}>Cancel</button>
            <button type="submit" data-testid="product-save" disabled={submitting} className="ji-btn-accent inline-flex items-center gap-2 px-5 h-10 text-sm font-semibold disabled:opacity-60" style={{ borderRadius: 4 }}>
              {submitting ? <Spinner size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
              {editing ? "Save Changes" : "Add Product"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ShopPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load products");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((p) => {
      if (filterCat !== "all" && p.category !== filterCat) return false;
      if (!ql) return true;
      return [p.name, p.brand, p.description, p.supplier].filter(Boolean).join(" ").toLowerCase().includes(ql);
    });
  }, [items, q, filterCat]);

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (p) => { setEditing(p); setModalOpen(true); };

  const toggleActive = async (p) => {
    try {
      const { data } = await api.patch(`/admin/products/${p.id}`, { is_active: !p.is_active });
      setItems((prev) => prev.map((it) => (it.id === p.id ? data : it)));
      toast.success(data.is_active ? "Product activated." : "Product hidden from shop.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      setItems((p) => p.filter((r) => r.id !== id));
      toast.success("Deleted.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="panel-shop" className="space-y-4">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
          <Input data-testid="shop-admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, brand, supplier…" className="h-11 pl-9" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger data-testid="shop-admin-filter" className="md:w-56 h-11"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {PRODUCT_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button onClick={load} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
        <button data-testid="shop-admin-add" onClick={openAdd} className="ji-btn-accent inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
          <Plus size={16} weight="bold" /> Add Product
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[var(--ji-muted)]"><Spinner size={22} className="animate-spin mx-auto" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="ji-card p-12 text-center text-[var(--ji-muted)]" style={{ borderRadius: 4 }}>
          No products match. Click <strong>Add Product</strong> to create one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => {
            const catLabel = PRODUCT_CATEGORIES.find((c) => c.id === p.category)?.label || p.category;
            return (
              <div key={p.id} data-testid={`shop-admin-row-${p.id}`} className="ji-card overflow-hidden" style={{ borderRadius: 4 }}>
                <div className="flex gap-4 p-4">
                  <div className="w-24 h-24 bg-[var(--ji-surface-alt)] flex-shrink-0 overflow-hidden" style={{ borderRadius: 4 }}>
                    <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" onError={(e) => (e.target.style.opacity = 0.2)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${p.is_active ? "bg-emerald-100 text-emerald-900" : "bg-stone-200 text-stone-700"}`} style={{ borderRadius: 4 }}>
                        {p.is_active ? "Active" : "Hidden"}
                      </span>
                      {p.badge && <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[var(--ji-accent)] text-white" style={{ borderRadius: 4 }}>{p.badge}</span>}
                    </div>
                    <div style={{ fontFamily: "Cabinet Grotesk" }} className="mt-1 text-base font-bold tracking-tight line-clamp-2">{p.name}</div>
                    <div className="text-xs text-[var(--ji-muted)] mt-1">
                      {catLabel} {p.brand ? `· ${p.brand}` : ""} {p.supplier ? `· via ${p.supplier}` : ""}
                    </div>
                    <div className="mt-2 flex items-baseline justify-between">
                      <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold text-[var(--ji-primary)]">${Number(p.price).toFixed(2)}</span>
                      <span className="text-[10px] text-[var(--ji-muted)] inline-flex items-center gap-1">
                        <ArrowUpRight size={10} weight="bold" /> {p.click_count || 0} clicks
                      </span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-[var(--ji-border)] p-3 flex flex-wrap gap-2 bg-[var(--ji-surface-alt)]">
                  <button onClick={() => openEdit(p)} data-testid={`shop-admin-edit-${p.id}`} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                  <button onClick={() => toggleActive(p)} data-testid={`shop-admin-toggle-${p.id}`} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                    {p.is_active ? <><EyeSlash size={12} weight="bold" /> Hide</> : <><Eye size={12} weight="bold" /> Show</>}
                  </button>
                  <a href={p.affiliate_url} target="_blank" rel="noreferrer" className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold" style={{ borderRadius: 4 }}>
                    <ArrowUpRight size={12} weight="bold" /> Open
                  </a>
                  <button onClick={() => remove(p.id)} className="ji-btn-outline inline-flex items-center gap-1 px-3 h-9 text-xs font-semibold text-red-600 hover:bg-red-50" style={{ borderRadius: 4 }}>
                    <Trash size={12} weight="bold" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ProductModal open={modalOpen} onOpenChange={setModalOpen} editing={editing} onSaved={load} />
    </div>
  );
};


// ---------- Command Center — Owner Overview ----------
const RANGE_LABELS = { today: "Today", week: "This Week", month: "This Month", all: "All Time" };

const RevenueTile = () => {
  const [range, setRange] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/revenue-summary");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load revenue");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bucket = data ? data[range] : null;
  const total = bucket?.total || 0;
  const src = bucket?.by_source || { diagnostic: {}, invoice: {}, deposit: {} };

  return (
    <div data-testid="command-tile-revenue" className="ji-card p-6" style={{ borderRadius: 4 }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="ji-overline inline-flex items-center gap-1"><CurrencyDollar size={12} weight="bold" /> Revenue Dashboard</div>
          <div style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1 }} className="text-4xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]" data-testid="revenue-total">
            {`$${total.toFixed(2)}`}
          </div>
          <div className="text-xs text-[var(--ji-muted)] mt-1">
            {bucket?.txn_count || 0} transactions · {RANGE_LABELS[range]}
          </div>
        </div>
        <button onClick={load} className="ji-btn-outline p-2" style={{ borderRadius: 4 }} title="Refresh">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {Object.entries(RANGE_LABELS).map(([k, v]) => (
          <button
            key={k}
            data-testid={`revenue-range-${k}`}
            onClick={() => setRange(k)}
            className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 font-semibold transition-colors ${
              range === k ? "bg-[var(--ji-primary)] text-white" : "bg-[var(--ji-surface-alt)] text-[var(--ji-muted)] hover:text-[var(--ji-text)]"
            }`}
            style={{ borderRadius: 4 }}
          >{v}</button>
        ))}
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between border-b border-[var(--ji-border)] pb-1">
          <span className="text-[var(--ji-muted)]">Diagnostic fees</span>
          <span className="font-semibold">${(src.diagnostic?.amount || 0).toFixed(2)} <span className="text-xs text-[var(--ji-muted)]">· {src.diagnostic?.count || 0}</span></span>
        </div>
        <div className="flex items-center justify-between border-b border-[var(--ji-border)] pb-1">
          <span className="text-[var(--ji-muted)]">Admin invoices</span>
          <span className="font-semibold">${(src.invoice?.amount || 0).toFixed(2)} <span className="text-xs text-[var(--ji-muted)]">· {src.invoice?.count || 0}</span></span>
        </div>
        <div className="flex items-center justify-between border-b border-[var(--ji-border)] pb-1">
          <span className="text-[var(--ji-muted)]">Customer deposits</span>
          <span className="font-semibold">${(src.deposit?.amount || 0).toFixed(2)} <span className="text-xs text-[var(--ji-muted)]">· {src.deposit?.count || 0}</span></span>
        </div>
        <div className="flex items-center justify-between pt-1 text-[var(--ji-accent)] font-semibold">
          <span className="inline-flex items-center gap-1"><TrendUp size={12} weight="bold" /> Pending invoices</span>
          <span data-testid="revenue-pending">${(data?.pending_invoices?.amount || 0).toFixed(2)} <span className="text-xs opacity-70">· {data?.pending_invoices?.count || 0}</span></span>
        </div>
      </div>
    </div>
  );
};

const LeadsTile = ({ onOpenTab }) => {
  const [stats, setStats] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const byStatus = stats.by_status || {};
  const pipeline = [
    { k: "new", label: "New", color: "bg-[var(--ji-accent)] text-white" },
    { k: "contacted", label: "Contacted", color: "bg-blue-100 text-blue-900" },
    { k: "scheduled", label: "Scheduled", color: "bg-amber-100 text-amber-900" },
    { k: "completed", label: "Completed", color: "bg-emerald-100 text-emerald-900" },
  ];

  return (
    <div data-testid="command-tile-leads" className="ji-card p-6" style={{ borderRadius: 4 }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="ji-overline inline-flex items-center gap-1"><Wrench size={12} weight="bold" /> Leads Pipeline</div>
          <div style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1 }} className="text-4xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]" data-testid="leads-total">
            {stats.total || 0}
          </div>
          <div className="text-xs text-[var(--ji-muted)] mt-1">Total service requests</div>
        </div>
        <button onClick={load} className="ji-btn-outline p-2" style={{ borderRadius: 4 }} title="Refresh">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {pipeline.map((p) => (
          <div key={p.k} className="ji-card p-3" style={{ borderRadius: 4, background: "var(--ji-surface-alt)" }}>
            <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${p.color}`} style={{ borderRadius: 4 }}>{p.label}</span>
            <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold mt-1" data-testid={`leads-${p.k}`}>{byStatus[p.k] || 0}</div>
          </div>
        ))}
      </div>

      <button
        onClick={onOpenTab}
        data-testid="leads-open-requests"
        className="ji-btn-outline w-full inline-flex items-center justify-center gap-2 h-10 text-xs font-semibold"
        style={{ borderRadius: 4 }}
      >
        <ArrowUpRight size={12} weight="bold" /> Open leads
      </button>
    </div>
  );
};

const TrafficTile = ({ onOpenTab }) => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("week");
  const load = useCallback(async (r) => {
    try {
      const { data } = await api.get(`/admin/traffic-summary?range=${r}`);
      setData(data);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(range); }, [load, range]);

  return (
    <div data-testid="command-tile-traffic" className="ji-card p-6" style={{ borderRadius: 4 }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="ji-overline inline-flex items-center gap-1"><ChartLineUp size={12} weight="bold" /> Traffic Insights</div>
          <div style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1 }} className="text-4xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]" data-testid="traffic-visitors">
            {data?.unique_visitors || 0}
          </div>
          <div className="text-xs text-[var(--ji-muted)] mt-1">Unique visitors · {RANGE_LABELS[range]}</div>
        </div>
        <button onClick={() => load(range)} className="ji-btn-outline p-2" style={{ borderRadius: 4 }} title="Refresh">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>

      <div className="flex flex-wrap gap-1 mb-4">
        {["today", "week", "month", "all"].map((k) => (
          <button
            key={k}
            data-testid={`traffic-range-${k}`}
            onClick={() => setRange(k)}
            className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 font-semibold transition-colors ${
              range === k ? "bg-[var(--ji-primary)] text-white" : "bg-[var(--ji-surface-alt)] text-[var(--ji-muted)] hover:text-[var(--ji-text)]"
            }`}
            style={{ borderRadius: 4 }}
          >{RANGE_LABELS[k]}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="ji-card p-3" style={{ borderRadius: 4, background: "var(--ji-surface-alt)" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><EyeIcon size={10} weight="bold" /> Pageviews</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold mt-1" data-testid="traffic-pageviews">{data?.pageviews || 0}</div>
        </div>
        <div className="ji-card p-3" style={{ borderRadius: 4, background: "var(--ji-surface-alt)" }}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><PaperPlaneTilt size={10} weight="bold" /> Form submits</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold mt-1" data-testid="traffic-submits">{data?.total_submits || 0}</div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs mb-3">
        <span className="text-[var(--ji-muted)]">Conversion rate</span>
        <span className="font-semibold text-[var(--ji-accent)]" data-testid="traffic-conversion">{data?.conversion_rate || 0}%</span>
      </div>

      <button
        onClick={onOpenTab}
        data-testid="traffic-open-tab"
        className="ji-btn-outline w-full inline-flex items-center justify-center gap-2 h-10 text-xs font-semibold"
        style={{ borderRadius: 4 }}
      >
        <ArrowUpRight size={12} weight="bold" /> Traffic details
      </button>
    </div>
  );
};

const ReviewsTile = ({ onOpenTab }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/reviews");
      setItems(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${id}`, { status });
      setItems((p) => p.map((r) => (r.id === id ? data : r)));
      toast.success(`Review ${status}.`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed"); }
  };

  const pending = items.filter((r) => r.status === "pending");
  const approved = items.filter((r) => r.status === "approved").length;

  return (
    <div data-testid="command-tile-reviews" className="ji-card p-6" style={{ borderRadius: 4 }}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="ji-overline inline-flex items-center gap-1"><ChatCircleDots size={12} weight="bold" /> Review Hub</div>
          <div style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1 }} className="text-4xl font-bold tracking-tight mt-2 text-[var(--ji-primary)]" data-testid="reviews-pending-count">
            {pending.length}
          </div>
          <div className="text-xs text-[var(--ji-muted)] mt-1">Pending · {approved} approved</div>
        </div>
        <button onClick={load} className="ji-btn-outline p-2" style={{ borderRadius: 4 }} title="Refresh">
          <ArrowsClockwise size={14} weight="bold" />
        </button>
      </div>

      <div className="space-y-3 mb-4 min-h-[80px]">
        {loading ? (
          <div className="text-center text-[var(--ji-muted)] text-sm py-4"><Spinner size={16} className="animate-spin mx-auto" /></div>
        ) : pending.length === 0 ? (
          <div className="text-center text-[var(--ji-muted)] text-sm py-4">All caught up. No pending reviews.</div>
        ) : (
          pending.slice(0, 2).map((r) => (
            <div key={r.id} className="ji-card p-3" style={{ borderRadius: 4, background: "var(--ji-surface-alt)" }}>
              <div className="flex items-center gap-1 mb-1">
                {[...Array(5)].map((_, k) => (
                  <Star key={k} size={11} weight={k < r.rating ? "fill" : "regular"} className={k < r.rating ? "text-[var(--ji-accent)]" : "text-[var(--ji-border)]"} />
                ))}
                <span className="text-xs text-[var(--ji-muted)] ml-1">· {r.name}</span>
              </div>
              <p className="text-xs leading-relaxed line-clamp-2">&ldquo;{r.quote}&rdquo;</p>
              <div className="flex gap-1 mt-2">
                <button
                  onClick={() => setStatus(r.id, "approved")}
                  data-testid={`command-review-approve-${r.id}`}
                  className="ji-btn-accent inline-flex items-center gap-1 px-2 h-7 text-[10px] font-semibold flex-1 justify-center"
                  style={{ borderRadius: 4 }}
                ><CheckCircle size={10} weight="bold" /> Approve</button>
                <button
                  onClick={() => setStatus(r.id, "rejected")}
                  data-testid={`command-review-reject-${r.id}`}
                  className="ji-btn-outline inline-flex items-center gap-1 px-2 h-7 text-[10px] font-semibold flex-1 justify-center text-red-600 hover:bg-red-50"
                  style={{ borderRadius: 4 }}
                ><Trash size={10} weight="bold" /> Reject</button>
              </div>
            </div>
          ))
        )}
      </div>

      <button
        onClick={onOpenTab}
        data-testid="reviews-open-tab"
        className="ji-btn-outline w-full inline-flex items-center justify-center gap-2 h-10 text-xs font-semibold"
        style={{ borderRadius: 4 }}
      >
        <ArrowUpRight size={12} weight="bold" /> Open review hub
      </button>
    </div>
  );
};

const CommandCenter = ({ onOpenTab }) => (
  <div data-testid="command-center" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-10">
    <RevenueTile />
    <LeadsTile onOpenTab={() => onOpenTab("requests")} />
    <TrafficTile onOpenTab={() => onOpenTab("traffic")} />
    <ReviewsTile onOpenTab={() => onOpenTab("reviews")} />
  </div>
);

// ---------- Traffic full-detail panel ----------
const TrafficPanel = () => {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("week");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/traffic-summary?range=${r}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to load traffic");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(range); }, [load, range]);

  return (
    <div data-testid="panel-traffic" className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center">
        {["today", "week", "month", "all"].map((k) => (
          <button
            key={k}
            data-testid={`traffic-panel-range-${k}`}
            onClick={() => setRange(k)}
            className={`text-xs uppercase tracking-[0.15em] px-3 py-2 font-semibold transition-colors ${
              range === k ? "bg-[var(--ji-primary)] text-white" : "bg-[var(--ji-surface-alt)] text-[var(--ji-muted)] hover:text-[var(--ji-text)]"
            }`}
            style={{ borderRadius: 4 }}
          >{RANGE_LABELS[k]}</button>
        ))}
        <button onClick={() => load(range)} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-10 text-sm font-semibold ml-auto" style={{ borderRadius: 4 }}>
          <ArrowsClockwise size={16} weight="bold" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><Users size={11} /> Unique visitors</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]">{data?.unique_visitors || 0}</div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><EyeIcon size={11} /> Pageviews</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]">{data?.pageviews || 0}</div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><PaperPlaneTilt size={11} /> Form submits</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]">{data?.total_submits || 0}</div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><TrendUp size={11} /> Conversion</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-accent)]">{data?.conversion_rate || 0}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="ji-overline mb-3">Top Pages</div>
          {loading ? (
            <div className="text-center text-[var(--ji-muted)] py-6"><Spinner size={18} className="animate-spin mx-auto" /></div>
          ) : (data?.top_pages || []).length === 0 ? (
            <div className="text-sm text-[var(--ji-muted)] py-6 text-center">No pageviews yet in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.top_pages.map((p, i) => {
                const max = data.top_pages[0]?.count || 1;
                const pct = Math.round((p.count / max) * 100);
                return (
                  <div key={p.path + i}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <code className="text-xs font-mono text-[var(--ji-text)]">{p.path}</code>
                      <span className="font-semibold text-[var(--ji-primary)]">{p.count}</span>
                    </div>
                    <div className="h-1.5 bg-[var(--ji-surface-alt)] overflow-hidden" style={{ borderRadius: 4 }}>
                      <div className="h-full bg-[var(--ji-accent)]" style={{ width: `${pct}%`, borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="ji-overline mb-3">Form Submissions</div>
          {loading ? (
            <div className="text-center text-[var(--ji-muted)] py-6"><Spinner size={18} className="animate-spin mx-auto" /></div>
          ) : (data?.form_submits || []).length === 0 ? (
            <div className="text-sm text-[var(--ji-muted)] py-6 text-center">No submissions yet in this window.</div>
          ) : (
            <div className="space-y-2">
              {data.form_submits.map((f, i) => (
                <div key={f.form + i} className="flex items-center justify-between border-b border-[var(--ji-border)] pb-2 last:border-0">
                  <span className="text-sm capitalize">{f.form.replace(/-/g, " ")}</span>
                  <span className="font-semibold text-[var(--ji-primary)]">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ---------- Main Admin Page ----------
export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState({});
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoicesRefreshKey, setInvoicesRefreshKey] = useState(0);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  const onInvoiceCreated = () => {
    setInvoicesRefreshKey((k) => k + 1);
    loadStats();
  };

  const TABS = [
    { id: "requests", label: "Service Requests", icon: Wrench, count: stats.total },
    { id: "traffic", label: "Traffic", icon: ChartLineUp },
    { id: "careers", label: "Careers", icon: Briefcase, count: stats.career_applications },
    { id: "reviews", label: "Reviews", icon: ChatCircleDots },
    { id: "invoices", label: "Invoices", icon: Receipt, count: stats.invoices_total },
    { id: "payments", label: "Diagnostics Paid", icon: CreditCard, count: stats.payments_total },
    { id: "shop", label: "Shop", icon: Storefront },
  ];

  return (
    <div data-testid="admin-page" className="ji-container py-12 md:py-16">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="ji-overline mb-2">Repair Dispatch LA</div>
          <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="admin-title">Owner Command Center</h1>
          <p className="mt-2 text-sm text-[var(--ji-muted)]" data-testid="admin-user-email">Signed in as {user?.email}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="admin-header-report-btn"
            onClick={async () => {
              try {
                const toastId = toast.loading("Sending weekly report…");
                await api.post("/admin/reports/weekly/send-now");
                toast.dismiss(toastId);
                toast.success("Weekly report sent to your inbox.");
              } catch (e) {
                toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Report send failed");
              }
            }}
            className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold"
            style={{ borderRadius: 4 }}
          >
            <PaperPlaneTilt size={16} weight="bold" /> Weekly Report
          </button>
          <button
            data-testid="admin-header-invoice-btn"
            onClick={() => setInvoiceModalOpen(true)}
            className="ji-btn-accent inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold"
            style={{ borderRadius: 4 }}
          >
            <PaperPlaneTilt size={16} weight="bold" /> Send Invoice
          </button>
          <button onClick={handleLogout} data-testid="admin-logout" className="ji-btn-primary inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      </div>

      {/* Owner Command Center — 4 module tiles */}
      <CommandCenter onOpenTab={(id) => { setTab(id); loadStats(); }} />

      {/* Legacy compact stats row (kept for quick glance below command center) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8" data-testid="admin-stats">
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><Wrench size={11} /> Requests</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]" data-testid="stat-requests">{stats.total || 0}</div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><Briefcase size={11} /> Applications</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]" data-testid="stat-careers">{stats.career_applications || 0}</div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><Receipt size={11} /> Invoices</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]" data-testid="stat-invoices">{stats.invoices_paid || 0}<span className="text-lg text-[var(--ji-muted)]">/{stats.invoices_total || 0}</span></div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><CreditCard size={11} /> Diagnostics</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-primary)]" data-testid="stat-payments">{stats.payments_paid || 0}<span className="text-lg text-[var(--ji-muted)]">/{stats.payments_total || 0}</span></div>
        </div>
        <div className="ji-card p-5" style={{ borderRadius: 4 }}>
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)] inline-flex items-center gap-1"><CurrencyDollar size={11} /> Revenue</div>
          <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-3xl font-bold mt-1 text-[var(--ji-accent)]" data-testid="stat-revenue">${Number((stats.revenue_paid || 0) + (stats.invoice_revenue || 0)).toFixed(2)}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-[var(--ji-border)]" data-testid="admin-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); loadStats(); }}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-[1px] transition-colors ${
                active ? "border-[var(--ji-accent)] text-[var(--ji-primary)]" : "border-transparent text-[var(--ji-muted)] hover:text-[var(--ji-text)]"
              }`}
            >
              <Icon size={16} weight="duotone" /> {t.label}
              {typeof t.count === "number" && (
                <span className="text-[10px] px-2 py-0.5 bg-[var(--ji-surface-alt)] text-[var(--ji-muted)]" style={{ borderRadius: 999 }}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "requests" && <ServiceRequestsPanel />}
      {tab === "traffic" && <TrafficPanel />}
      {tab === "careers" && <CareerAppsPanel />}
      {tab === "reviews" && <ReviewsPanel />}
      {tab === "invoices" && <InvoicesPanel onOpenModal={() => setInvoiceModalOpen(true)} refreshKey={invoicesRefreshKey} />}
      {tab === "payments" && <PaymentsPanel />}
      {tab === "shop" && <ShopPanel />}

      <InvoiceModal open={invoiceModalOpen} onOpenChange={setInvoiceModalOpen} onCreated={onInvoiceCreated} />
    </div>
  );
}
